import { create } from 'zustand'

import { DEFAULT_WIDTH_FOR_DAY } from './Calendar.constants'

import { CalendarMode, Period, Pointer, ResizingEvent, EventType } from './Calendar.types'

export interface CalendarState {
  mode: CalendarMode
  dayWidth: number
  pointer?: Pointer
  selectedZoneId?: string
  selectedEvents: EventType[]
  selectedPeriod?: Period
  visiblePeriod?: {
    startDate: string
    endDate: string
    duration: number
  }
  resizingEvent?: ResizingEvent
  isDragging: boolean
  isSelectionModeActive: boolean
  isPanModeActive: boolean
}

const getInitialState = (): CalendarState => ({
  mode: CalendarMode.Day21,
  dayWidth: DEFAULT_WIDTH_FOR_DAY,
  pointer: undefined,
  selectedZoneId: undefined,
  selectedEvents: [],
  resizingEvent: undefined,
  isDragging: false,
  isSelectionModeActive: false,
  isPanModeActive: false,
})

interface CalendarStore {
  state: CalendarState
  actions: {
    setMode: (mode: CalendarMode) => void
    setDayWidth: (dayWidth: number) => void
    setPointer: (pointer?: Pointer) => void
    setSelectedZoneId: (selectedZoneId?: string) => void
    setSelectedEvent: (event: EventType) => void
    setSelectedEvents: (events: EventType[]) => void
    setSelectedPeriod: (period?: Period) => void
    setVisiblePeriod: (visiblePeriod: CalendarState['visiblePeriod']) => void
    setResizingEvent: (resizingEvent?: ResizingEvent) => void
    setIsDragging: (isDragging: boolean) => void
    toggleSelectionMode: () => void
    setSelectionModeActive: (isSelectionModeActive: boolean) => void
    setIsPanModeActive: (isPanModeActive: boolean) => void
  }
}

export const useCalendarStore = create<CalendarStore>((set, get) => ({
  state: getInitialState(),
  actions: {
    setMode: (mode: CalendarMode) => set(({ state }) => ({ state: { ...state, mode } })),
    setDayWidth: (dayWidth: number) => set(({ state }) => ({ state: { ...state, dayWidth } })),
    setPointer: (pointer?: Pointer) => {
      const { pointer: pointerState } = get().state

      if (pointerState?.rowId != pointer?.rowId || pointerState?.startX != pointer?.startX) {
        set(({ state }) => ({ state: { ...state, pointer } }))
      } else if (!pointer) {
        set(({ state }) => ({ state: { ...state, pointer: undefined } }))
      }
    },
    setSelectedZoneId: (selectedZoneId?: string) => {
      set(({ state }) => ({
        state: {
          ...state,
          selectedZoneId: state.selectedZoneId == selectedZoneId ? undefined : selectedZoneId,
          selectedEvents: [],
        },
      }))
    },
    setSelectedEvent: (event: EventType) => {
      const { selectedEvents } = get().state

      const filteredEvents = selectedEvents.filter((t) => t.id != event.id)

      if (selectedEvents[0] && selectedEvents[0].zoneId != event.zoneId) {
        set(({ state }) => ({ state: { ...state, selectedEvents: [event], selectedZoneId: undefined } }))
      } else if (filteredEvents.length == selectedEvents.length) {
        set(({ state }) => ({
          state: { ...state, selectedEvents: [...selectedEvents, event], selectedZoneId: undefined },
        }))
      } else {
        set(({ state }) => ({ state: { ...state, selectedEvents: filteredEvents, selectedZoneId: undefined } }))
      }
    },
    setSelectedEvents: (events: EventType[]) => {
      const { selectedEvents } = get().state

      const currentEventsSelection = selectedEvents.map(({ id }) => id).join(',')
      const nextEventsSelection = events.map(({ id }) => id).join(',')

      if (currentEventsSelection == nextEventsSelection) return

      set(({ state }) => ({ state: { ...state, selectedEvents: events, selectedZoneId: undefined } }))
    },
    setSelectedPeriod: (period?: Period) => {
      const { selectedPeriod } = get().state

      if (selectedPeriod?.duration == period?.duration && selectedPeriod?.startDate == period?.startDate) {
        return
      }

      set(({ state }) => ({ state: { ...state, selectedPeriod: period } }))
    },
    setResizingEvent: (resizingEvent?: ResizingEvent) => set(({ state }) => ({ state: { ...state, resizingEvent } })),
    setIsDragging: (isDragging: boolean) => set(({ state }) => ({ state: { ...state, isDragging } })),
    toggleSelectionMode: () =>
      set(({ state }) => ({
        state: { ...state, isSelectionModeActive: !state.isSelectionModeActive, isPanModeActive: false },
      })),
    setSelectionModeActive: (isSelectionModeActive: boolean) =>
      set(({ state }) => ({
        state: { ...state, isSelectionModeActive, isPanModeActive: false },
      })),
    setIsPanModeActive: (isPanModeActive: boolean) =>
      set(({ state }) => ({
        state: { ...state, isPanModeActive, isSelectionModeActive: false },
      })),
    setVisiblePeriod: (visiblePeriod: CalendarState['visiblePeriod']) => {
      set(({ state }) => ({ state: { ...state, visiblePeriod } }))
    },
  },
}))
